import instance from '../api';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface EldKey {
  id: number;
  name: string;
  eld_identifier: string;
  registration_id: string;
  eld_provider: string;
  eld_model_number: string | null;
  is_default: boolean;
  has_public_key: boolean;
  has_private_key: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EldKeyBody {
  name?: string;
  eld_identifier?: string;
  registration_id?: string;
  eld_provider?: string;
  eld_model_number?: string | null;
  public_key?: string;
  private_key?: string;
  is_default?: boolean;
}

export interface CompanyEldKey {
  companyId: number;
  eldKeyId: number | null;
  usesDefault: boolean;
  eldKeyName: string | null;
  eldProvider?: string | null;
  eldModelNumber?: string | null;
}

export type MigrationDirection = 'to_legal' | 'to_non_legal';
export type MigrationStatusValue = 'running' | 'done' | 'partial' | 'failed';

export interface MigrationError {
  driverId: number;
  // Present when only that day failed; absent when the driver's whole import failed
  date?: string;
  windowStart?: string;
  message: string;
}

export interface MigrationStatus {
  migrationId: number;
  direction: MigrationDirection;
  status: MigrationStatusValue;
  driversTotal: number;
  driversDone: number;
  driversFailed: number;
  driversPending: number;
  errors: MigrationError[];
  startedAt: string;
  finishedAt: string | null;
  pendingDriverIds?: number[];
  driversRetried?: number[];
}

// Backend error shape: { error: { statusCode, message, data } }
export const apiErrorMessage = (error: any, fallback = 'Something went wrong') =>
  error?.response?.data?.error?.message || error?.message || fallback;

const migrationPath = (direction: MigrationDirection) =>
  direction === 'to_legal' ? 'admins/migrate-to-legal' : 'admins/migrate-to-non-legal';

// ── API ────────────────────────────────────────────────────────────────────────

export const legalMigration = {
  // ELD key sets
  async listEldKeys(): Promise<EldKey[]> {
    const { data } = await instance.get('admins/eld-keys');
    return data;
  },
  async createEldKey(body: EldKeyBody): Promise<EldKey> {
    const { data } = await instance.post('admins/eld-keys', body);
    return data;
  },
  async updateEldKey(id: number, body: EldKeyBody): Promise<EldKey> {
    const { data } = await instance.patch(`admins/eld-keys/${id}`, body);
    return data;
  },
  async deleteEldKey(id: number) {
    const { data } = await instance.delete(`admins/eld-keys/${id}`);
    return data;
  },
  async setDefaultEldKey(id: number): Promise<EldKey> {
    const { data } = await instance.post(`admins/eld-keys/${id}/set-default`);
    return data;
  },
  async companyEldKey(companyId: string | number): Promise<CompanyEldKey> {
    const { data } = await instance.get(`admins/eld-keys/company/${companyId}`);
    return data;
  },

  // Company migration (drivers, vehicles and logs)
  async migrateToLegal(companyId: string | number, eldKeyId?: number | null) {
    const body = eldKeyId ? { eldKeyId } : {};
    const { data } = await instance.post(`admins/migrate-to-legal/${companyId}`, body);
    return data;
  },
  async migrateToNonLegal(companyId: string | number) {
    const { data } = await instance.post(`admins/migrate-to-non-legal/${companyId}`);
    return data;
  },
  // Both directions share one status service; returns null when the company was never migrated
  async migrationStatus(companyId: string | number): Promise<MigrationStatus | null> {
    try {
      const { data } = await instance.get(`admins/migrate-to-legal/${companyId}/status`);
      return data;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  },
  async retryMigration(companyId: string | number, direction: MigrationDirection) {
    const { data } = await instance.post(`${migrationPath(direction)}/${companyId}/retry`);
    return data;
  },

  // Subscription price (allowed plans depend on the company's current ELD type)
  async changeSubscriptionPrice(companyId: string | number, priceId?: string) {
    const body = priceId ? { priceId } : {};
    const { data } = await instance.patch(`admins/company-subscription-price/${companyId}`, body);
    return data;
  },
};
